import { isAttachmentStrategyUsingCheckoutButtons } from "@redotech/redo-model/returns/attachment-strategy";
import { parseGid } from "@redotech/redo-model/shopify/gid-utils";
import {
  WidgetAutocheck,
  WidgetConfig,
  WidgetFees,
} from "@redotech/redo-model/widget";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { useEffect, useState } from "react";
import { RedoToggleElement } from "../cart-app";
import {
  DEFAULT_CHECKOUT_BUTTONS_SELECTOR,
  DEFAULT_PRODUCT_SELECTOR,
} from "../selectors";
import { getCartShadowRoot } from "../utils";
import { hasValidButtonSelectors } from "./checkout-button-utils";

export const useHideRedoProductAndCheckoutButtons = (
  autocheck: WidgetAutocheck,
  fees: WidgetFees,
  toggles: {
    redoToggleElements: RedoToggleElement[];
    widgetConfig?: WidgetConfig;
  }[],
  cart: ShopifyCart | undefined,
) => {
  const [foundValidButtonSelectors, setFoundValidButtonSelectors] = useState<
    boolean | undefined
  >(undefined);
  const redoIndex = cart?.items.findIndex((item) =>
    [
      Number(fees.packageProduct?.productId),
      Number(fees.returnProduct?.productId),
      Number(fees.bothProduct?.productId),
    ].includes(Number(item.product_id)),
  );

  useEffect(() => {
    if (!autocheck.attachmentStrategy) {
      return;
    }

    if (
      !isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]
    ) {
      return;
    }

    const valid = hasValidButtonSelectors(toggles);

    if (foundValidButtonSelectors !== valid) {
      setFoundValidButtonSelectors(valid);
    }
  }, [toggles, autocheck.attachmentStrategy, foundValidButtonSelectors]);

  useEffect(() => {
    if (!fees || !autocheck.hideRedoProduct?.enabled) {
      return;
    }

    if (
      redoStorefront?.type !== "commercecloud" &&
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      foundValidButtonSelectors === false
    ) {
      return;
    }

    const products = [
      fees.packageProduct,
      fees.returnProduct,
      fees.bothProduct,
    ];

    let productSelector =
      autocheck.hideRedoProduct.productSelector || DEFAULT_PRODUCT_SELECTOR;

    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      foundValidButtonSelectors !== false
    ) {
      productSelector += `, ${autocheck.hideRedoProduct.checkoutButtonSelector || DEFAULT_CHECKOUT_BUTTONS_SELECTOR}`;
    }

    const selectors: string[] = [];
    for (const product of products) {
      if (!product) {
        continue;
      }
      const variants =
        product.variants.length >= 1 ? product.variants : [{ variant_id: "" }];
      for (const variant of variants) {
        selectors.push(
          productSelector
            .replaceAll("%handle%", product.handle || "")
            ?.replaceAll("%product_id%", String(product.productId) || "")
            ?.replaceAll(
              "%variant_id%",
              product.variants?.length >= 1
                ? parseGid(String(variant.variant_id))
                : "",
            )
            ?.replaceAll(
              "%index%",
              redoIndex
                ? String(redoIndex + 1)
                : "1000000" /* Some nonsensical child index that hopefully won't select the wrong thing */,
            ),
        );
      }
    }
    const selector = selectors.filter(Boolean).join(", ");
    if (!selector) {
      return;
    }
    const style = document.createElement("style");
    style.innerText = `${selector} {display: none !important;}`;
    document.head.append(style);

    // If the cart is inside a shadow root, append the styles there
    const shadowRoot = getCartShadowRoot(autocheck);

    let shadowStyle: HTMLStyleElement | undefined = undefined;
    if (shadowRoot) {
      shadowStyle = document.createElement("style");
      shadowStyle.innerText = `${selector} {display: none !important;}`;
      shadowRoot.append(shadowStyle);
    }

    return () => {
      style.remove();
      shadowStyle?.remove();
    };
  }, [
    fees,
    autocheck,
    autocheck.hideRedoProduct?.enabled,
    foundValidButtonSelectors,
    cart,
    redoIndex,
  ]);
};
